import React, { useState, useRef, useEffect } from 'react';
    import { motion, AnimatePresence } from 'framer-motion';
    import * as FiIcons from 'react-icons/fi';
    import SafeIcon from './SafeIcon';

    const { FiChevronDown, FiChevronRight, FiCheck } = FiIcons;

    const RelationshipSelect = ({ id, value, onChange, relationshipGroups, required }) => {
      const [isOpen, setIsOpen] = useState(false);
      const [openAccordions, setOpenAccordions] = useState({
        'FAMILY': true, // Open by default
        '2nd Generation': false,
        '3rd Generation+': false,
        'In-Laws': false,
      });
      const wrapperRef = useRef(null);

      useEffect(() => {
        function handleClickOutside(event) {
          if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
            setIsOpen(false);
          }
        }
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
          document.removeEventListener("touchstart", handleClickOutside);
        };
      }, [wrapperRef]);

      const handleSelect = (type) => {
        onChange(type);
        setIsOpen(false);
      };

      const toggleAccordion = (groupName) => {
        setOpenAccordions(prev => ({ ...prev, [groupName]: !prev[groupName] }));
      };

      const allOptions = relationshipGroups.flatMap(g => g.options);
      const selectedOption = allOptions.find(opt => opt.type === value);
      const displayLabel = selectedOption ? `${selectedOption.group}: ${selectedOption.label}` : 'Select relationship';

      const groupedData = relationshipGroups.map(mainGroup => {
        const subGroups = {};
        mainGroup.options.forEach(option => {
          if (!subGroups[option.group]) {
            subGroups[option.group] = [];
          }
          subGroups[option.group].push(option);
        });
        return { ...mainGroup, subGroups };
      });

      return (
        <div className="relative" ref={wrapperRef}>
          <button
            type="button"
            id={id}
            onClick={() => setIsOpen(!isOpen)}
            className="w-full pl-3 pr-10 py-2 text-left border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center bg-white"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <span className={value ? 'text-gray-800' : 'text-gray-500'}>{displayLabel}</span>
            <SafeIcon icon={FiChevronDown} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute z-10 bottom-full mb-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-80 overflow-y-auto"
                role="listbox"
              >
                {groupedData.map(mainGroup => {
                  if (mainGroup.name === 'FAMILY') {
                    return (
                      <div key={mainGroup.name}>
                        <button
                          type="button"
                          onClick={() => toggleAccordion('FAMILY')}
                          className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-blue-600 bg-gray-50 hover:bg-gray-100 sticky top-0 z-10 uppercase"
                        >
                          <span>{mainGroup.name}</span>
                          <SafeIcon icon={openAccordions['FAMILY'] ? FiChevronDown : FiChevronRight} />
                        </button>
                        {openAccordions['FAMILY'] && (
                          <div className="pl-2">
                            {Object.entries(mainGroup.subGroups).map(([subGroupName, options]) => {
                              const isNestedAccordion = ['2nd Generation', '3rd Generation+', 'In-Laws'].includes(subGroupName);
                              if (isNestedAccordion) {
                                return (
                                  <div key={subGroupName} className="border-l border-gray-200">
                                    <button
                                      type="button"
                                      onClick={() => toggleAccordion(subGroupName)}
                                      className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-blue-600 hover:bg-gray-100 uppercase"
                                    >
                                      <span>{subGroupName}</span>
                                      <SafeIcon icon={openAccordions[subGroupName] ? FiChevronDown : FiChevronRight} />
                                    </button>
                                    {openAccordions[subGroupName] && (
                                      <div className="pl-4 border-l-2 border-gray-100">
                                        {options.map(option => (
                                          <div
                                            key={option.type}
                                            onClick={() => handleSelect(option.type)}
                                            className={`px-3 py-2 cursor-pointer hover:bg-blue-50 flex items-center justify-between ${
                                              value === option.type ? 'font-semibold text-blue-600' : 'text-gray-800'
                                            }`}
                                            role="option"
                                            aria-selected={value === option.type}
                                          >
                                            <span>{option.label}</span>
                                            {value === option.type && <SafeIcon icon={FiCheck} className="text-blue-600" />}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              } else {
                                // 1st Generation
                                return (
                                  <div key={subGroupName}>
                                    <div className="px-3 pt-2 text-xs font-semibold text-gray-400">{subGroupName}</div>
                                    {options.map(option => (
                                      <div
                                        key={option.type}
                                        onClick={() => handleSelect(option.type)}
                                        className={`px-3 py-2 cursor-pointer hover:bg-blue-50 flex items-center justify-between ${
                                          value === option.type ? 'font-semibold text-blue-600' : 'text-gray-800'
                                        }`}
                                        role="option"
                                        aria-selected={value === option.type}
                                      >
                                        <span>{option.label}</span>
                                        {value === option.type && <SafeIcon icon={FiCheck} className="text-blue-600" />}
                                      </div>
                                    ))}
                                  </div>
                                );
                              }
                            })}
                          </div>
                        )}
                      </div>
                    );
                  } else {
                    // Other groups like "OTHER RELATIONSHIPS"
                    return (
                      <div key={mainGroup.name}>
                        <h3 className="px-3 py-2 text-sm font-semibold text-blue-600 bg-gray-50 sticky top-0 uppercase">{mainGroup.name}</h3>
                        {Object.entries(mainGroup.subGroups).map(([subGroupName, options]) => (
                          <div key={subGroupName}>
                            <div className="px-3 pt-2 text-xs font-semibold text-gray-400">{subGroupName}</div>
                            {options.map(option => (
                              <div
                                key={option.type}
                                onClick={() => handleSelect(option.type)}
                                className={`px-3 py-2 cursor-pointer hover:bg-blue-50 flex items-center justify-between ${
                                  value === option.type ? 'font-semibold text-blue-600' : 'text-gray-800'
                                }`}
                                role="option"
                                aria-selected={value === option.type}
                              >
                                <span>{option.label}</span>
                                {value === option.type && <SafeIcon icon={FiCheck} className="text-blue-600" />}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  }
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    };

    export default RelationshipSelect;